// ===========================================
// MUSIC DEMO PAGE - Lofi Generator with Tone.js
// ===========================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Tone from 'tone';

// ===========================================
// TYPES
// ===========================================

type InstrumentType = 'polysynth' | 'fmsynth' | 'pluck' | 'piano';

interface SynthSettings {
  instrument: InstrumentType;
  type: 'sine' | 'square' | 'sawtooth' | 'triangle' | 'fmsine' | 'amsine'; // oscillator type for polysynth
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  volume: number;
}

interface FilterSettings {
  type: 'lowpass' | 'highpass' | 'bandpass';
  frequency: number;
  Q: number;
}

interface DrumSettings {
  kickVolume: number;
  snareVolume: number;
  hihatVolume: number;
  swing: number;
  enabled: boolean;
}

interface EffectsSettings {
  reverbWet: number;
  reverbDecay: number;
  delayWet: number;
  delayTime: number;
  delayFeedback: number;
  chorusWet: number;
  chorusFrequency: number;
  chorusDepth: number;
  bitcrusherBits: number;
  bitcrusherWet: number;
}

interface AmbientSettings {
  vinylNoiseVolume: number;
  rainVolume: number;
  tapeWobbleAmount: number;
}

interface BassSettings {
  enabled: boolean;
  volume: number;
  octave: number; // -2, -1, 0 (relative to chord root)
  attack: number;
  decay: number;
  sustain: number;
  release: number;
}

interface ArpSettings {
  enabled: boolean;
  pattern: 'up' | 'down' | 'upDown' | 'random' | 'randomOnce';
  rate: '4n' | '8n' | '8t' | '16n' | '16t'; // note values (t = triplet)
  octaves: number; // 1-3
  velocity: number; // 0-1
}

interface Preset {
  name: string;
  bpm: number;
  synth: SynthSettings;
  filter: FilterSettings;
  drums: DrumSettings;
  effects: EffectsSettings;
  ambient: AmbientSettings;
  bass: BassSettings;
  arp: ArpSettings;
  chordProgression: string;
}

interface CustomPreset extends Preset {
  saved: boolean;
}

// Custom presets storage key
const CUSTOM_PRESETS_KEY = 'lofi-generator-custom-presets';

// Initialize empty custom presets
const createEmptyCustomPresets = (): CustomPreset[] => {
  return Array.from({ length: 8 }, (_, i) => ({
    name: `Custom ${i + 1}`,
    bpm: DEFAULT_BPM,
    synth: { ...DEFAULT_SYNTH },
    filter: { ...DEFAULT_FILTER },
    drums: { ...DEFAULT_DRUMS },
    effects: { ...DEFAULT_EFFECTS },
    ambient: { ...DEFAULT_AMBIENT },
    bass: { ...DEFAULT_BASS },
    arp: { ...DEFAULT_ARP },
    chordProgression: 'lofi-jazz',
    saved: false,
  }));
};

// Load custom presets from localStorage
const loadCustomPresets = (): CustomPreset[] => {
  try {
    const stored = localStorage.getItem(CUSTOM_PRESETS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure we have 8 presets, fill in any missing ones
      const presets = createEmptyCustomPresets();
      for (let i = 0; i < Math.min(parsed.length, 8); i++) {
        if (parsed[i] && parsed[i].saved) {
          presets[i] = { ...presets[i], ...parsed[i] };
        }
      }
      return presets;
    }
  } catch (e) {
    console.error('Failed to load custom presets:', e);
  }
  return createEmptyCustomPresets();
};

// ===========================================
// CHORD PROGRESSIONS
// ===========================================

const CHORD_PROGRESSIONS: Record<string, string[][]> = {
  'lofi-jazz': [
    ['Dm7', 'G7', 'Cmaj7', 'Am7'],
    ['Fmaj7', 'Bm7b5', 'E7', 'Am7'],
  ],
  'chill-hop': [
    ['Am7', 'Dm7', 'G7', 'Cmaj7'],
    ['Fmaj7', 'Em7', 'Dm7', 'E7'],
  ],
  'jazzy': [
    ['Cmaj7', 'Am7', 'Dm7', 'G7'],
    ['Em7', 'A7', 'Dm7', 'G7'],
  ],
  'melancholy': [
    ['Am7', 'Em7', 'Fmaj7', 'Cmaj7'],
    ['Dm7', 'Am7', 'E7', 'Am7'],
  ],
  'dreamy': [
    ['Fmaj7', 'Cmaj7', 'Am7', 'Em7'],
    ['Dm7', 'G7', 'Cmaj7', 'Am7'],
  ],
  'neo-soul': [
    ['Dm9', 'G13', 'Cmaj9', 'Am9'],
    ['Fmaj9', 'Bb13', 'Am9', 'Gm9'],
  ],
  'interstellar': [
    // Inspired by Hans Zimmer's Interstellar themes (First Step, Cornfield Chase)
    // Key of A minor, slow sustained organ swells, emotional minor progressions
    ['Am', 'Amsus2', 'Em', 'Emsus4'],
    ['Fmaj7', 'C', 'G', 'Am'],
    ['Am', 'G', 'Fmaj7', 'Em'],
    ['Fmaj7', 'Gsus4', 'Am', 'Am'],
  ],
};

// Chord to notes mapping
const CHORD_NOTES: Record<string, string[]> = {
  // Basic triads (open voicings for ambient/cinematic)
  'C': ['C3', 'E3', 'G3'],
  'G': ['G2', 'B2', 'D3', 'G3'],
  'Am': ['A2', 'E3', 'A3', 'C4'],        // Open voicing
  'Em': ['E2', 'B2', 'E3', 'G3'],        // Open voicing

  // Suspended chords (Zimmer loves these)
  'Amsus2': ['A2', 'B2', 'E3', 'A3'],    // A suspended 2nd
  'Emsus4': ['E2', 'A2', 'B2', 'E3'],    // E minor suspended 4th
  'Gsus4': ['G2', 'C3', 'D3', 'G3'],     // G suspended 4th

  // 7th chords
  'Cmaj7': ['C3', 'E3', 'G3', 'B3'],
  'Cmaj9': ['C3', 'E3', 'G3', 'B3', 'D4'],
  'Dm7': ['D3', 'F3', 'A3', 'C4'],
  'Dm9': ['D3', 'F3', 'A3', 'C4', 'E4'],
  'Em7': ['E3', 'G3', 'B3', 'D4'],
  'Fmaj7': ['F3', 'A3', 'C4', 'E4'],
  'Fmaj9': ['F3', 'A3', 'C4', 'E4', 'G4'],
  'G7': ['G2', 'B2', 'D3', 'F3'],
  'G13': ['G2', 'B2', 'D3', 'F3', 'E4'],
  'Am7': ['A2', 'C3', 'E3', 'G3'],
  'Am9': ['A2', 'C3', 'E3', 'G3', 'B3'],
  'Bm7b5': ['B2', 'D3', 'F3', 'A3'],
  'E7': ['E3', 'G#3', 'B3', 'D4'],
  'A7': ['A2', 'C#3', 'E3', 'G3'],
  'Gm9': ['G2', 'Bb2', 'D3', 'F3', 'A3'],
  'Bb13': ['Bb2', 'D3', 'F3', 'Ab3', 'G4'],
};

// ===========================================
// DEFAULTS (Classic Lofi)
// ===========================================

const DEFAULT_SYNTH: SynthSettings = { instrument: 'polysynth', type: 'triangle', attack: 0.1, decay: 0.3, sustain: 0.4, release: 1.2, volume: -12 };

// Salamander Piano sample URLs (hosted on Tone.js CDN)
const PIANO_SAMPLES: Record<string, string> = {
  A0: 'A0.mp3', C1: 'C1.mp3', 'D#1': 'Ds1.mp3', 'F#1': 'Fs1.mp3', A1: 'A1.mp3',
  C2: 'C2.mp3', 'D#2': 'Ds2.mp3', 'F#2': 'Fs2.mp3', A2: 'A2.mp3',
  C3: 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3', A3: 'A3.mp3',
  C4: 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3', A4: 'A4.mp3',
  C5: 'C5.mp3', 'D#5': 'Ds5.mp3', 'F#5': 'Fs5.mp3', A5: 'A5.mp3',
  C6: 'C6.mp3', 'D#6': 'Ds6.mp3', 'F#6': 'Fs6.mp3', A6: 'A6.mp3',
  C7: 'C7.mp3', 'D#7': 'Ds7.mp3', 'F#7': 'Fs7.mp3', A7: 'A7.mp3',
  C8: 'C8.mp3',
};
const PIANO_BASE_URL = 'https://tonejs.github.io/audio/salamander/';
const DEFAULT_FILTER: FilterSettings = { type: 'lowpass', frequency: 2000, Q: 1 };
const DEFAULT_DRUMS: DrumSettings = { kickVolume: -8, snareVolume: -10, hihatVolume: -14, swing: 0.1, enabled: true };
const DEFAULT_EFFECTS: EffectsSettings = { reverbWet: 0.4, reverbDecay: 2.5, delayWet: 0.2, delayTime: 0.375, delayFeedback: 0.3, chorusWet: 0.1, chorusFrequency: 1.5, chorusDepth: 0.5, bitcrusherBits: 12, bitcrusherWet: 0.15 };
const DEFAULT_AMBIENT: AmbientSettings = { vinylNoiseVolume: -24, rainVolume: -30, tapeWobbleAmount: 0.02 };
const DEFAULT_BASS: BassSettings = { enabled: true, volume: -8, octave: -1, attack: 0.02, decay: 0.3, sustain: 0.6, release: 0.5 };
const DEFAULT_ARP: ArpSettings = { enabled: false, pattern: 'up', rate: '8n', octaves: 1, velocity: 0.6 };
const DEFAULT_BPM = 75;

// Bass root notes for each chord (lowest note)
const CHORD_BASS_NOTES: Record<string, string> = {
  // Basic triads
  'C': 'C2', 'G': 'G1', 'Am': 'A1', 'Em': 'E2',
  // Suspended
  'Amsus2': 'A1', 'Emsus4': 'E2', 'Gsus4': 'G1',
  // 7ths
  'Cmaj7': 'C2', 'Cmaj9': 'C2',
  'Dm7': 'D2', 'Dm9': 'D2',
  'Em7': 'E2',
  'Fmaj7': 'F2', 'Fmaj9': 'F2',
  'G7': 'G1', 'G13': 'G1',
  'Am7': 'A1', 'Am9': 'A1',
  'Bm7b5': 'B1',
  'E7': 'E2',
  'A7': 'A1',
  'Gm9': 'G1',
  'Bb13': 'Bb1',
};

// Arpeggio pattern generators
const generateArpPattern = (notes: string[], pattern: ArpSettings['pattern'], octaves: number): string[] => {
  // Extend notes across octaves
  const extendedNotes: string[] = [];
  for (let oct = 0; oct < octaves; oct++) {
    notes.forEach(note => {
      const match = note.match(/([A-G]#?)(\d)/);
      if (match) {
        const [, pitch, octave] = match;
        extendedNotes.push(`${pitch}${parseInt(octave) + oct}`);
      }
    });
  }

  switch (pattern) {
    case 'up':
      return extendedNotes;
    case 'down':
      return [...extendedNotes].reverse();
    case 'upDown':
      const upDown = [...extendedNotes, ...extendedNotes.slice(1, -1).reverse()];
      return upDown;
    case 'random':
      return extendedNotes; // Will be randomized on each play
    case 'randomOnce':
      return [...extendedNotes].sort(() => Math.random() - 0.5);
    default:
      return extendedNotes;
  }
};

// ===========================================
// PRESETS
// ===========================================

const PRESETS: Preset[] = [
  {
    name: 'Deep Space',
    bpm: 55,
    synth: { instrument: 'piano', type: 'sine', attack: 0.8, decay: 0.5, sustain: 0.9, release: 3, volume: -10 },
    filter: { type: 'lowpass', frequency: 1400, Q: 0.3 },
    drums: { kickVolume: -40, snareVolume: -40, hihatVolume: -40, swing: 0, enabled: false },
    effects: { reverbWet: 0.85, reverbDecay: 10, delayWet: 0.35, delayTime: 0.8, delayFeedback: 0.45, chorusWet: 0.2, chorusFrequency: 0.3, chorusDepth: 0.8, bitcrusherBits: 16, bitcrusherWet: 0 },
    ambient: { vinylNoiseVolume: -35, rainVolume: -40, tapeWobbleAmount: 0 },
    bass: { enabled: true, volume: -10, octave: -2, attack: 0.5, decay: 0.8, sustain: 0.9, release: 2.0 },
    arp: { enabled: false, pattern: 'up', rate: '4n', octaves: 1, velocity: 0.3 },
    chordProgression: 'interstellar',
  },
  {
    name: 'Casino Chill',
    bpm: 72,
    synth: { instrument: 'fmsynth', type: 'fmsine', attack: 0.15, decay: 0.35, sustain: 0.5, release: 1.8, volume: -13 },
    filter: { type: 'lowpass', frequency: 2200, Q: 0.8 },
    drums: { kickVolume: -12, snareVolume: -14, hihatVolume: -16, swing: 0.22, enabled: true },
    effects: { reverbWet: 0.45, reverbDecay: 3.2, delayWet: 0.18, delayTime: 0.4, delayFeedback: 0.28, chorusWet: 0.12, chorusFrequency: 1.2, chorusDepth: 0.45, bitcrusherBits: 14, bitcrusherWet: 0.08 },
    ambient: { vinylNoiseVolume: -22, rainVolume: -40, tapeWobbleAmount: 0.018 },
    bass: { enabled: true, volume: -7, octave: -1, attack: 0.03, decay: 0.35, sustain: 0.65, release: 0.6 },
    arp: { enabled: true, pattern: 'up', rate: '8n', octaves: 1, velocity: 0.45 },
    chordProgression: 'neo-soul',
  },
  {
    name: 'Classic Lofi',
    bpm: 75,
    synth: { instrument: 'polysynth', type: 'triangle', attack: 0.1, decay: 0.3, sustain: 0.4, release: 1.2, volume: -12 },
    filter: { type: 'lowpass', frequency: 2000, Q: 1 },
    drums: { kickVolume: -8, snareVolume: -10, hihatVolume: -14, swing: 0.1, enabled: true },
    effects: { reverbWet: 0.4, reverbDecay: 2.5, delayWet: 0.2, delayTime: 0.375, delayFeedback: 0.3, chorusWet: 0.1, chorusFrequency: 1.5, chorusDepth: 0.5, bitcrusherBits: 12, bitcrusherWet: 0.15 },
    ambient: { vinylNoiseVolume: -24, rainVolume: -30, tapeWobbleAmount: 0.02 },
    bass: { enabled: true, volume: -8, octave: -1, attack: 0.02, decay: 0.3, sustain: 0.6, release: 0.5 },
    arp: { enabled: false, pattern: 'up', rate: '8n', octaves: 1, velocity: 0.6 },
    chordProgression: 'lofi-jazz',
  },
  {
    name: 'Rainy Day',
    bpm: 68,
    synth: { instrument: 'polysynth', type: 'sine', attack: 0.2, decay: 0.4, sustain: 0.5, release: 1.5, volume: -14 },
    filter: { type: 'lowpass', frequency: 1500, Q: 0.8 },
    drums: { kickVolume: -10, snareVolume: -12, hihatVolume: -16, swing: 0.15, enabled: true },
    effects: { reverbWet: 0.6, reverbDecay: 4, delayWet: 0.3, delayTime: 0.5, delayFeedback: 0.4, chorusWet: 0.15, chorusFrequency: 1, chorusDepth: 0.7, bitcrusherBits: 10, bitcrusherWet: 0.2 },
    ambient: { vinylNoiseVolume: -22, rainVolume: -18, tapeWobbleAmount: 0.03 },
    bass: { enabled: true, volume: -10, octave: -1, attack: 0.05, decay: 0.4, sustain: 0.5, release: 0.8 },
    arp: { enabled: true, pattern: 'up', rate: '8n', octaves: 1, velocity: 0.5 },
    chordProgression: 'melancholy',
  },
  {
    name: 'Late Night Jazz',
    bpm: 82,
    synth: { instrument: 'fmsynth', type: 'fmsine', attack: 0.05, decay: 0.2, sustain: 0.6, release: 0.8, volume: -10 },
    filter: { type: 'lowpass', frequency: 3000, Q: 1.2 },
    drums: { kickVolume: -6, snareVolume: -8, hihatVolume: -12, swing: 0.2, enabled: true },
    effects: { reverbWet: 0.35, reverbDecay: 2, delayWet: 0.15, delayTime: 0.25, delayFeedback: 0.25, chorusWet: 0.05, chorusFrequency: 2, chorusDepth: 0.3, bitcrusherBits: 14, bitcrusherWet: 0.08 },
    ambient: { vinylNoiseVolume: -26, rainVolume: -40, tapeWobbleAmount: 0.015 },
    bass: { enabled: true, volume: -6, octave: -1, attack: 0.01, decay: 0.2, sustain: 0.7, release: 0.3 },
    arp: { enabled: false, pattern: 'upDown', rate: '8t', octaves: 1, velocity: 0.7 },
    chordProgression: 'jazzy',
  },
  {
    name: 'Dreamy',
    bpm: 65,
    synth: { instrument: 'polysynth', type: 'sine', attack: 0.3, decay: 0.5, sustain: 0.6, release: 2, volume: -14 },
    filter: { type: 'lowpass', frequency: 1200, Q: 0.5 },
    drums: { kickVolume: -14, snareVolume: -16, hihatVolume: -20, swing: 0.08, enabled: true },
    effects: { reverbWet: 0.7, reverbDecay: 5, delayWet: 0.4, delayTime: 0.6, delayFeedback: 0.5, chorusWet: 0.25, chorusFrequency: 0.8, chorusDepth: 0.8, bitcrusherBits: 8, bitcrusherWet: 0.25 },
    ambient: { vinylNoiseVolume: -20, rainVolume: -25, tapeWobbleAmount: 0.04 },
    bass: { enabled: true, volume: -12, octave: -1, attack: 0.1, decay: 0.5, sustain: 0.4, release: 1.0 },
    arp: { enabled: true, pattern: 'upDown', rate: '8n', octaves: 2, velocity: 0.4 },
    chordProgression: 'dreamy',
  },
  {
    name: 'Chill Hop',
    bpm: 88,
    synth: { instrument: 'polysynth', type: 'sawtooth', attack: 0.02, decay: 0.15, sustain: 0.3, release: 0.5, volume: -16 },
    filter: { type: 'lowpass', frequency: 2500, Q: 1.5 },
    drums: { kickVolume: -4, snareVolume: -6, hihatVolume: -10, swing: 0.12, enabled: true },
    effects: { reverbWet: 0.25, reverbDecay: 1.5, delayWet: 0.1, delayTime: 0.188, delayFeedback: 0.2, chorusWet: 0.08, chorusFrequency: 2.5, chorusDepth: 0.4, bitcrusherBits: 16, bitcrusherWet: 0.05 },
    ambient: { vinylNoiseVolume: -28, rainVolume: -40, tapeWobbleAmount: 0.01 },
    bass: { enabled: true, volume: -4, octave: -1, attack: 0.01, decay: 0.15, sustain: 0.8, release: 0.2 },
    arp: { enabled: true, pattern: 'random', rate: '16n', octaves: 1, velocity: 0.5 },
    chordProgression: 'chill-hop',
  },
  {
    name: 'Neo Soul',
    bpm: 72,
    synth: { instrument: 'polysynth', type: 'amsine', attack: 0.08, decay: 0.25, sustain: 0.5, release: 1, volume: -11 },
    filter: { type: 'lowpass', frequency: 2800, Q: 1.3 },
    drums: { kickVolume: -5, snareVolume: -7, hihatVolume: -11, swing: 0.18, enabled: true },
    effects: { reverbWet: 0.3, reverbDecay: 2.2, delayWet: 0.18, delayTime: 0.33, delayFeedback: 0.28, chorusWet: 0.12, chorusFrequency: 1.8, chorusDepth: 0.55, bitcrusherBits: 12, bitcrusherWet: 0.1 },
    ambient: { vinylNoiseVolume: -25, rainVolume: -35, tapeWobbleAmount: 0.025 },
    bass: { enabled: true, volume: -5, octave: -1, attack: 0.02, decay: 0.25, sustain: 0.7, release: 0.4 },
    arp: { enabled: false, pattern: 'up', rate: '8t', octaves: 1, velocity: 0.6 },
    chordProgression: 'neo-soul',
  },
  {
    name: 'Minimal',
    bpm: 70,
    synth: { instrument: 'pluck', type: 'sine', attack: 0.15, decay: 0.3, sustain: 0.4, release: 1, volume: -15 },
    filter: { type: 'lowpass', frequency: 1800, Q: 0.7 },
    drums: { kickVolume: -20, snareVolume: -20, hihatVolume: -20, swing: 0.05, enabled: false },
    effects: { reverbWet: 0.5, reverbDecay: 3, delayWet: 0.25, delayTime: 0.4, delayFeedback: 0.35, chorusWet: 0.15, chorusFrequency: 1.2, chorusDepth: 0.6, bitcrusherBits: 14, bitcrusherWet: 0.1 },
    ambient: { vinylNoiseVolume: -22, rainVolume: -40, tapeWobbleAmount: 0.02 },
    bass: { enabled: false, volume: -15, octave: -1, attack: 0.1, decay: 0.3, sustain: 0.5, release: 0.8 },
    arp: { enabled: true, pattern: 'up', rate: '4n', octaves: 2, velocity: 0.4 },
    chordProgression: 'dreamy',
  },
  {
    name: 'Vinyl Only',
    bpm: 75,
    synth: { instrument: 'polysynth', type: 'triangle', attack: 0.1, decay: 0.3, sustain: 0.4, release: 1.2, volume: -40 },
    filter: { type: 'lowpass', frequency: 2000, Q: 1 },
    drums: { kickVolume: -40, snareVolume: -40, hihatVolume: -40, swing: 0.1, enabled: false },
    effects: { reverbWet: 0.3, reverbDecay: 2, delayWet: 0.15, delayTime: 0.3, delayFeedback: 0.2, chorusWet: 0.05, chorusFrequency: 1.5, chorusDepth: 0.3, bitcrusherBits: 16, bitcrusherWet: 0 },
    ambient: { vinylNoiseVolume: -12, rainVolume: -40, tapeWobbleAmount: 0.03 },
    bass: { enabled: false, volume: -40, octave: -1, attack: 0.02, decay: 0.3, sustain: 0.6, release: 0.5 },
    arp: { enabled: false, pattern: 'up', rate: '8n', octaves: 1, velocity: 0.6 },
    chordProgression: 'lofi-jazz',
  },
];

// ===========================================
// SLIDER COMPONENT
// ===========================================

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function Slider({ label, value, min, max, step = 0.01, unit = '', onChange, disabled = false }: SliderProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-mono">{value.toFixed(step >= 1 ? 0 : 2)}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
  );
}

// ===========================================
// SECTION HEADER COMPONENT
// ===========================================

interface SectionHeaderProps {
  title: string;
  onReset: () => void;
}

function SectionHeader({ title, onReset }: SectionHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold text-purple-400">{title}</h2>
      <button
        onClick={onReset}
        className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
        title="Reset to defaults"
      >
        Reset
      </button>
    </div>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export function MusicDemoPage() {
  const navigate = useNavigate();

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [bpm, setBpm] = useState(75);
  const [masterVolume, setMasterVolume] = useState(-6);
  const [currentChord, setCurrentChord] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<string>('Deep Space');

  // Settings state
  const [synthSettings, setSynthSettings] = useState<SynthSettings>(PRESETS[0].synth);
  const [filterSettings, setFilterSettings] = useState<FilterSettings>(PRESETS[0].filter);
  const [drumSettings, setDrumSettings] = useState<DrumSettings>(PRESETS[0].drums);
  const [effectsSettings, setEffectsSettings] = useState<EffectsSettings>(PRESETS[0].effects);
  const [ambientSettings, setAmbientSettings] = useState<AmbientSettings>(PRESETS[0].ambient);
  const [bassSettings, setBassSettings] = useState<BassSettings>(PRESETS[0].bass);
  const [arpSettings, setArpSettings] = useState<ArpSettings>(PRESETS[0].arp);
  const [chordProgression, setChordProgression] = useState<string>('lofi-jazz');

  // Custom presets
  const [customPresets, setCustomPresets] = useState<CustomPreset[]>(loadCustomPresets);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Tone.js refs - Multiple instrument types
  const synthRef = useRef<Tone.PolySynth | null>(null);          // Basic poly synth
  const fmSynthRef = useRef<Tone.PolySynth | null>(null);        // FM synth (Rhodes-like)
  const pluckSynthRef = useRef<Tone.PluckSynth | null>(null);    // Plucked strings
  const pianoRef = useRef<Tone.Sampler | null>(null);            // Salamander Piano
  const [pianoLoaded, setPianoLoaded] = useState(false);
  const [loadingPiano, setLoadingPiano] = useState(false);
  const filterRef = useRef<Tone.Filter | null>(null);
  const reverbRef = useRef<Tone.Reverb | null>(null);
  const delayRef = useRef<Tone.FeedbackDelay | null>(null);
  const chorusRef = useRef<Tone.Chorus | null>(null);
  const bitcrusherRef = useRef<Tone.BitCrusher | null>(null);
  const vinylNoiseRef = useRef<Tone.Noise | null>(null);
  const vinylFilterRef = useRef<Tone.Filter | null>(null);
  const vinylGainRef = useRef<Tone.Gain | null>(null);
  const kickRef = useRef<Tone.MembraneSynth | null>(null);
  const snareRef = useRef<Tone.NoiseSynth | null>(null);
  const hihatRef = useRef<Tone.MetalSynth | null>(null);
  const drumGainsRef = useRef<{ kick: Tone.Gain; snare: Tone.Gain; hihat: Tone.Gain } | null>(null);
  const loopRef = useRef<Tone.Loop | null>(null);
  const drumLoopRef = useRef<Tone.Sequence | null>(null);
  const bassRef = useRef<Tone.MonoSynth | null>(null);
  const bassGainRef = useRef<Tone.Gain | null>(null);
  const bassLoopRef = useRef<Tone.Loop | null>(null);
  const arpLoopRef = useRef<Tone.Sequence | null>(null);
  const chordIndexRef = useRef(0);
  const progressionIndexRef = useRef(0);
  const currentChordNotesRef = useRef<string[]>([]);
  const currentChordNameRef = useRef<string>('');

  // Settings refs (for real-time updates in loops)
  const synthSettingsRef = useRef(synthSettings);
  const bassSettingsRef = useRef(bassSettings);
  const arpSettingsRef = useRef(arpSettings);
  const drumSettingsRef = useRef(drumSettings);
  const chordProgressionRef = useRef(chordProgression);

  // Keep settings refs in sync
  useEffect(() => { synthSettingsRef.current = synthSettings; }, [synthSettings]);
  useEffect(() => { bassSettingsRef.current = bassSettings; }, [bassSettings]);
  useEffect(() => { arpSettingsRef.current = arpSettings; }, [arpSettings]);
  useEffect(() => { drumSettingsRef.current = drumSettings; }, [drumSettings]);
  useEffect(() => { chordProgressionRef.current = chordProgression; }, [chordProgression]);

  // Helper to get the active instrument based on settings
  const getActiveInstrument = useCallback(() => {
    const instrument = synthSettingsRef.current.instrument;
    switch (instrument) {
      case 'fmsynth':
        return fmSynthRef.current;
      case 'piano':
        return pianoLoaded ? pianoRef.current : synthRef.current; // Fallback to polysynth if piano not loaded
      case 'pluck':
        return pluckSynthRef.current;
      case 'polysynth':
      default:
        return synthRef.current;
    }
  }, [pianoLoaded]);

  // Initialize Tone.js audio context
  const initAudio = useCallback(async () => {
    if (isInitialized) return;

    await Tone.start();
    console.log('Audio context started');

    // Create effects chain
    reverbRef.current = new Tone.Reverb({
      decay: effectsSettings.reverbDecay,
      wet: effectsSettings.reverbWet,
    }).toDestination();

    delayRef.current = new Tone.FeedbackDelay({
      delayTime: effectsSettings.delayTime,
      feedback: effectsSettings.delayFeedback,
      wet: effectsSettings.delayWet,
    }).connect(reverbRef.current);

    chorusRef.current = new Tone.Chorus({
      frequency: effectsSettings.chorusFrequency,
      depth: effectsSettings.chorusDepth,
      wet: effectsSettings.chorusWet,
    }).connect(delayRef.current);

    bitcrusherRef.current = new Tone.BitCrusher(effectsSettings.bitcrusherBits).connect(chorusRef.current);

    // Create filter
    filterRef.current = new Tone.Filter({
      type: filterSettings.type,
      frequency: filterSettings.frequency,
      Q: filterSettings.Q,
    }).connect(bitcrusherRef.current);

    // Create synth (PolySynth with basic oscillators)
    synthRef.current = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: synthSettings.type as OscillatorType },
      envelope: {
        attack: synthSettings.attack,
        decay: synthSettings.decay,
        sustain: synthSettings.sustain,
        release: synthSettings.release,
      },
      volume: synthSettings.volume,
    }).connect(filterRef.current);

    // Create FM Synth (Rhodes-like electric piano)
    fmSynthRef.current = new Tone.PolySynth(Tone.FMSynth, {
      harmonicity: 3,
      modulationIndex: 10,
      envelope: {
        attack: synthSettings.attack,
        decay: synthSettings.decay,
        sustain: synthSettings.sustain,
        release: synthSettings.release,
      },
      modulation: { type: 'sine' },
      modulationEnvelope: {
        attack: 0.01,
        decay: 0.2,
        sustain: 0.5,
        release: 0.5,
      },
      volume: synthSettings.volume,
    }).connect(filterRef.current);

    // Create PluckSynth (guitar/harp-like plucked strings)
    // Note: PluckSynth is monophonic, so we use it differently
    pluckSynthRef.current = new Tone.PluckSynth({
      attackNoise: 1,
      dampening: 4000,
      resonance: 0.98,
      release: 1,
      volume: synthSettings.volume,
    }).connect(filterRef.current);

    // Create Salamander Piano Sampler (async load)
    setLoadingPiano(true);
    pianoRef.current = new Tone.Sampler({
      urls: PIANO_SAMPLES,
      baseUrl: PIANO_BASE_URL,
      onload: () => {
        console.log('Piano samples loaded');
        setPianoLoaded(true);
        setLoadingPiano(false);
      },
      onerror: (err) => {
        console.error('Failed to load piano samples:', err);
        setLoadingPiano(false);
      },
    }).connect(filterRef.current);

    // Create vinyl noise
    vinylFilterRef.current = new Tone.Filter({
      type: 'bandpass',
      frequency: 1000,
      Q: 0.5,
    }).toDestination();

    vinylGainRef.current = new Tone.Gain(Tone.dbToGain(ambientSettings.vinylNoiseVolume)).connect(vinylFilterRef.current);

    vinylNoiseRef.current = new Tone.Noise({
      type: 'brown',
      playbackRate: 0.5,
    }).connect(vinylGainRef.current);

    // Create drum sounds
    const drumReverb = new Tone.Reverb({ decay: 0.5, wet: 0.2 }).toDestination();

    const kickGain = new Tone.Gain(Tone.dbToGain(drumSettings.kickVolume)).connect(drumReverb);
    const snareGain = new Tone.Gain(Tone.dbToGain(drumSettings.snareVolume)).connect(drumReverb);
    const hihatGain = new Tone.Gain(Tone.dbToGain(drumSettings.hihatVolume)).connect(drumReverb);

    drumGainsRef.current = { kick: kickGain, snare: snareGain, hihat: hihatGain };

    kickRef.current = new Tone.MembraneSynth({
      pitchDecay: 0.05,
      octaves: 6,
      oscillator: { type: 'sine' },
      envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 0.4 },
    }).connect(kickGain);

    snareRef.current = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 },
    }).connect(snareGain);

    hihatRef.current = new Tone.MetalSynth({
      envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
      harmonicity: 5.1,
      modulationIndex: 32,
      resonance: 4000,
      octaves: 1.5,
    }).connect(hihatGain);

    // Create bass synth
    const bassReverb = new Tone.Reverb({ decay: 1.5, wet: 0.15 }).toDestination();
    const bassFilter = new Tone.Filter({ type: 'lowpass', frequency: 800, Q: 1 }).connect(bassReverb);
    bassGainRef.current = new Tone.Gain(Tone.dbToGain(bassSettings.volume)).connect(bassFilter);

    bassRef.current = new Tone.MonoSynth({
      oscillator: { type: 'triangle' },
      envelope: {
        attack: bassSettings.attack,
        decay: bassSettings.decay,
        sustain: bassSettings.sustain,
        release: bassSettings.release,
      },
      filterEnvelope: {
        attack: 0.01,
        decay: 0.2,
        sustain: 0.5,
        release: 0.5,
        baseFrequency: 200,
        octaves: 2,
      },
    }).connect(bassGainRef.current);

    setIsInitialized(true);
  }, [isInitialized, effectsSettings, filterSettings, synthSettings, ambientSettings, drumSettings, bassSettings]);

  // Update synth settings for all instrument types
  useEffect(() => {
    // Update PolySynth
    if (synthRef.current) {
      synthRef.current.set({
        oscillator: { type: synthSettings.type as OscillatorType },
        envelope: {
          attack: synthSettings.attack,
          decay: synthSettings.decay,
          sustain: synthSettings.sustain,
          release: synthSettings.release,
        },
        volume: synthSettings.volume,
      });
    }

    // Update FM Synth
    if (fmSynthRef.current) {
      fmSynthRef.current.set({
        envelope: {
          attack: synthSettings.attack,
          decay: synthSettings.decay,
          sustain: synthSettings.sustain,
          release: synthSettings.release,
        },
        volume: synthSettings.volume,
      });
    }

    // Update PluckSynth (limited parameters)
    if (pluckSynthRef.current) {
      pluckSynthRef.current.set({
        release: synthSettings.release,
        volume: synthSettings.volume,
      });
    }

    // Update Piano Sampler (volume and release only)
    if (pianoRef.current) {
      pianoRef.current.set({
        release: synthSettings.release,
        volume: synthSettings.volume,
      });
    }
  }, [synthSettings]);

  // Update filter settings
  useEffect(() => {
    if (!filterRef.current) return;
    filterRef.current.set({
      type: filterSettings.type,
      frequency: filterSettings.frequency,
      Q: filterSettings.Q,
    });
  }, [filterSettings]);

  // Update effects settings
  useEffect(() => {
    if (reverbRef.current) {
      reverbRef.current.set({ decay: effectsSettings.reverbDecay, wet: effectsSettings.reverbWet });
    }
    if (delayRef.current) {
      delayRef.current.set({
        delayTime: effectsSettings.delayTime,
        feedback: effectsSettings.delayFeedback,
        wet: effectsSettings.delayWet,
      });
    }
    if (chorusRef.current) {
      chorusRef.current.set({
        frequency: effectsSettings.chorusFrequency,
        depth: effectsSettings.chorusDepth,
        wet: effectsSettings.chorusWet,
      });
    }
    // Note: BitCrusher bits is read-only after construction, changes apply on restart
  }, [effectsSettings]);

  // Update ambient settings
  useEffect(() => {
    if (vinylGainRef.current) {
      vinylGainRef.current.gain.value = Tone.dbToGain(ambientSettings.vinylNoiseVolume);
    }
  }, [ambientSettings]);

  // Update drum volumes
  useEffect(() => {
    if (drumGainsRef.current) {
      drumGainsRef.current.kick.gain.value = Tone.dbToGain(drumSettings.kickVolume);
      drumGainsRef.current.snare.gain.value = Tone.dbToGain(drumSettings.snareVolume);
      drumGainsRef.current.hihat.gain.value = Tone.dbToGain(drumSettings.hihatVolume);
    }
  }, [drumSettings.kickVolume, drumSettings.snareVolume, drumSettings.hihatVolume]);

  // Update bass settings
  useEffect(() => {
    if (bassGainRef.current) {
      bassGainRef.current.gain.value = Tone.dbToGain(bassSettings.volume);
    }
    if (bassRef.current) {
      bassRef.current.set({
        envelope: {
          attack: bassSettings.attack,
          decay: bassSettings.decay,
          sustain: bassSettings.sustain,
          release: bassSettings.release,
        },
      });
    }
  }, [bassSettings]);

  // Update BPM
  useEffect(() => {
    Tone.getTransport().bpm.value = bpm;
  }, [bpm]);

  // Update master volume
  useEffect(() => {
    Tone.getDestination().volume.value = masterVolume;
  }, [masterVolume]);

  // Play/Stop
  const togglePlay = async () => {
    if (!isInitialized) {
      await initAudio();
    }

    if (isPlaying) {
      // Stop
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
      vinylNoiseRef.current?.stop();
      loopRef.current?.stop();
      drumLoopRef.current?.stop();
      bassLoopRef.current?.stop();
      arpLoopRef.current?.stop();
      setIsPlaying(false);
      setCurrentChord('');
    } else {
      // Start
      Tone.getTransport().bpm.value = bpm;

      // Start vinyl noise
      vinylNoiseRef.current?.start();

      // Create chord loop - reads from refs for real-time updates
      chordIndexRef.current = 0;
      progressionIndexRef.current = 0;

      loopRef.current = new Tone.Loop((time) => {
        const prog = chordProgressionRef.current;
        const progression = CHORD_PROGRESSIONS[prog];
        if (!progression) return;

        const currentProgression = progression[progressionIndexRef.current];
        const chordName = currentProgression[chordIndexRef.current];
        const notes = CHORD_NOTES[chordName];

        if (notes) {
          // Store current chord for other loops
          currentChordNotesRef.current = notes;
          currentChordNameRef.current = chordName;

          // Only play block chords if arpeggiation is disabled (check ref!)
          if (!arpSettingsRef.current.enabled) {
            const activeInstrument = getActiveInstrument();
            if (activeInstrument) {
              const velocity = 0.5 + Math.random() * 0.3;
              const timing = time + (Math.random() * 0.02 - 0.01);

              // PluckSynth is monophonic, play notes individually with slight stagger
              if (synthSettingsRef.current.instrument === 'pluck') {
                notes.forEach((note, i) => {
                  (activeInstrument as Tone.PluckSynth).triggerAttack(note, timing + i * 0.05);
                });
              } else {
                (activeInstrument as Tone.PolySynth | Tone.Sampler).triggerAttackRelease(notes, '2n', timing, velocity);
              }
            }
          }

          setCurrentChord(chordName);
        }

        chordIndexRef.current++;
        if (chordIndexRef.current >= currentProgression.length) {
          chordIndexRef.current = 0;
          progressionIndexRef.current = (progressionIndexRef.current + 1) % progression.length;
        }
      }, '2n');

      // Create arpeggiator - always runs, checks ref for enabled state
      let arpIndex = 0;
      let lastChordForArp = '';

      arpLoopRef.current = new Tone.Sequence(
        (time, _step) => {
          const arp = arpSettingsRef.current;
          if (!arp.enabled) return; // Check ref each time

          const notes = currentChordNotesRef.current;
          const activeInstrument = getActiveInstrument();
          if (!notes || notes.length === 0 || !activeInstrument) return;

          // Regenerate pattern on chord change or for random mode
          const currentChord = currentChordNameRef.current;
          const needNewPattern = lastChordForArp !== currentChord || arp.pattern === 'random';

          if (needNewPattern) {
            lastChordForArp = currentChord;
            if (arp.pattern !== 'random') {
              arpIndex = 0;
            }
          }

          // Generate pattern with current settings (reads from ref)
          const pattern = generateArpPattern(notes, arp.pattern, arp.octaves);

          // Get the current note
          let noteIndex: number;
          if (arp.pattern === 'random') {
            noteIndex = Math.floor(Math.random() * pattern.length);
          } else {
            noteIndex = arpIndex % pattern.length;
            arpIndex++;
          }

          const note = pattern[noteIndex];
          if (note) {
            const velocity = arp.velocity * (0.8 + Math.random() * 0.4);
            const humanize = (Math.random() * 0.01) - 0.005;

            // PluckSynth only supports triggerAttack (no duration)
            if (synthSettingsRef.current.instrument === 'pluck') {
              (activeInstrument as Tone.PluckSynth).triggerAttack(note, time + humanize);
            } else {
              (activeInstrument as Tone.PolySynth | Tone.Sampler).triggerAttackRelease(note, '8n', time + humanize, velocity);
            }
          }
        },
        Array.from({ length: 16 }, (_, i) => i),
        '8n' // Fixed rate, velocity/feel controlled by settings
      );

      arpLoopRef.current.start(0);

      // Create bass pattern - always runs, checks ref for enabled state
      bassLoopRef.current = new Tone.Loop((time) => {
        const bass = bassSettingsRef.current;
        if (!bass.enabled) return; // Check ref each time

        const chordName = currentChordNameRef.current;
        const bassNote = CHORD_BASS_NOTES[chordName];

        if (bassNote && bassRef.current) {
          const match = bassNote.match(/([A-G]#?)(\d)/);
          if (match) {
            const [, pitch, octave] = match;
            const adjustedOctave = parseInt(octave) + bass.octave;
            const finalNote = `${pitch}${Math.max(0, Math.min(3, adjustedOctave))}`;

            const velocity = 0.6 + Math.random() * 0.3;
            const timing = time + (Math.random() * 0.01 - 0.005);

            bassRef.current.triggerAttackRelease(finalNote, '4n', timing, velocity);
          }
        }
      }, '2n');

      bassLoopRef.current.start(0);

      // Create drum pattern - always runs, checks ref for enabled state
      drumLoopRef.current = new Tone.Sequence(
        (time, step) => {
          const drums = drumSettingsRef.current;
          if (!drums.enabled) return; // Check ref each time

          const swing = step % 2 === 1 ? drums.swing * 0.1 : 0;

          // Kick on 1 and 3
          if (step === 0 || step === 4) {
            kickRef.current?.triggerAttackRelease('C1', '8n', time + swing);
          }

          // Snare on 2 and 4
          if (step === 2 || step === 6) {
            snareRef.current?.triggerAttackRelease('8n', time + swing);
          }

          // Hi-hat on every 8th note
          const hihatVelocity = step % 2 === 0 ? 0.8 : 0.4;
          hihatRef.current?.triggerAttackRelease('32n', time + swing, hihatVelocity);
        },
        [0, 1, 2, 3, 4, 5, 6, 7],
        '8n'
      );

      drumLoopRef.current.start(0);

      loopRef.current.start(0);
      Tone.getTransport().start();
      setIsPlaying(true);
    }
  };

  // Apply preset
  const applyPreset = (presetName: string) => {
    const preset = PRESETS.find(p => p.name === presetName);
    if (!preset) return;

    setSelectedPreset(presetName);
    setBpm(preset.bpm);
    setSynthSettings(preset.synth);
    setFilterSettings(preset.filter);
    setDrumSettings(preset.drums);
    setEffectsSettings(preset.effects);
    setAmbientSettings(preset.ambient);
    setBassSettings(preset.bass);
    setArpSettings(preset.arp);
    setChordProgression(preset.chordProgression);
  };

  // Save custom presets to localStorage
  const saveCustomPresetsToStorage = (presets: CustomPreset[]) => {
    try {
      localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(presets));
    } catch (e) {
      console.error('Failed to save custom presets:', e);
    }
  };

  // Save current settings to a custom preset slot
  const saveToCustomPreset = (slotIndex: number) => {
    const newPresets = [...customPresets];
    newPresets[slotIndex] = {
      name: `Custom ${slotIndex + 1}`,
      bpm,
      synth: { ...synthSettings },
      filter: { ...filterSettings },
      drums: { ...drumSettings },
      effects: { ...effectsSettings },
      ambient: { ...ambientSettings },
      bass: { ...bassSettings },
      arp: { ...arpSettings },
      chordProgression,
      saved: true,
    };
    setCustomPresets(newPresets);
    saveCustomPresetsToStorage(newPresets);
    setSelectedPreset(`Custom ${slotIndex + 1}`);
    setShowSaveModal(false);
  };

  // Apply a custom preset
  const applyCustomPreset = (slotIndex: number) => {
    const preset = customPresets[slotIndex];
    if (!preset.saved) return;

    setSelectedPreset(preset.name);
    setBpm(preset.bpm);
    setSynthSettings(preset.synth);
    setFilterSettings(preset.filter);
    setDrumSettings(preset.drums);
    setEffectsSettings(preset.effects);
    setAmbientSettings(preset.ambient);
    setBassSettings(preset.bass);
    setArpSettings(preset.arp);
    setChordProgression(preset.chordProgression);
  };

  // Clear a custom preset slot
  const clearCustomPreset = (slotIndex: number) => {
    const newPresets = [...customPresets];
    newPresets[slotIndex] = {
      name: `Custom ${slotIndex + 1}`,
      bpm: DEFAULT_BPM,
      synth: { ...DEFAULT_SYNTH },
      filter: { ...DEFAULT_FILTER },
      drums: { ...DEFAULT_DRUMS },
      effects: { ...DEFAULT_EFFECTS },
      ambient: { ...DEFAULT_AMBIENT },
      bass: { ...DEFAULT_BASS },
      arp: { ...DEFAULT_ARP },
      chordProgression: 'lofi-jazz',
      saved: false,
    };
    setCustomPresets(newPresets);
    saveCustomPresetsToStorage(newPresets);
    if (selectedPreset === `Custom ${slotIndex + 1}`) {
      setSelectedPreset('');
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
      synthRef.current?.dispose();
      fmSynthRef.current?.dispose();
      pluckSynthRef.current?.dispose();
      pianoRef.current?.dispose();
      filterRef.current?.dispose();
      reverbRef.current?.dispose();
      delayRef.current?.dispose();
      chorusRef.current?.dispose();
      bitcrusherRef.current?.dispose();
      vinylNoiseRef.current?.dispose();
      vinylFilterRef.current?.dispose();
      vinylGainRef.current?.dispose();
      kickRef.current?.dispose();
      snareRef.current?.dispose();
      hihatRef.current?.dispose();
      bassRef.current?.dispose();
      bassGainRef.current?.dispose();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-purple-400">Lofi Generator</h1>
            <p className="text-gray-400">Procedural lofi music with Tone.js</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Master Controls */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-6">
            {/* Play/Stop Button */}
            <button
              onClick={togglePlay}
              className={`w-20 h-20 rounded-full flex items-center justify-center text-4xl transition-all ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-400 shadow-lg shadow-red-500/50'
                  : 'bg-purple-500 hover:bg-purple-400 shadow-lg shadow-purple-500/50'
              }`}
            >
              {isPlaying ? '⏹' : '▶'}
            </button>

            {/* Current Chord Display */}
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm text-gray-400 mb-1">Now Playing</div>
              <div className="text-4xl font-bold text-purple-400 font-mono">
                {currentChord || '---'}
              </div>
            </div>

            {/* BPM */}
            <div className="w-48">
              <Slider
                label="BPM"
                value={bpm}
                min={50}
                max={120}
                step={1}
                onChange={setBpm}
              />
            </div>

            {/* Master Volume */}
            <div className="w-48">
              <Slider
                label="Master Volume"
                value={masterVolume}
                min={-40}
                max={0}
                step={1}
                unit=" dB"
                onChange={setMasterVolume}
              />
            </div>
          </div>
        </div>

        {/* Presets */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-purple-400 mb-4">Presets</h2>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset.name)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  selectedPreset === preset.name
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {preset.name}
              </button>
            ))}
          </div>

          {/* Custom Presets */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-semibold text-purple-300">Custom Presets</h3>
              <button
                onClick={() => setShowSaveModal(true)}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <span>💾</span>
                Save Current
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {customPresets.map((preset, index) => (
                <div key={index} className="relative group">
                  <button
                    onClick={() => preset.saved && applyCustomPreset(index)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      selectedPreset === preset.name && preset.saved
                        ? 'bg-purple-500 text-white'
                        : preset.saved
                          ? 'bg-blue-700 text-gray-200 hover:bg-blue-600'
                          : 'bg-gray-700 text-gray-500 border border-dashed border-gray-600'
                    }`}
                    disabled={!preset.saved}
                    title={preset.saved ? `Load ${preset.name}` : 'Empty slot - save current settings here'}
                  >
                    {preset.name}
                    {preset.saved && <span className="ml-1 text-xs">✓</span>}
                  </button>
                  {preset.saved && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        clearCustomPreset(index);
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-400 text-white text-xs rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Clear preset"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save to Custom Preset Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-xl font-bold text-purple-400 mb-4">Save to Custom Preset</h3>
              <p className="text-gray-400 mb-6">Choose a slot to save your current settings:</p>

              <div className="grid grid-cols-4 gap-3 mb-6">
                {customPresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => saveToCustomPreset(index)}
                    className={`p-3 rounded-lg transition-all flex flex-col items-center gap-1 ${
                      preset.saved
                        ? 'bg-yellow-600 hover:bg-yellow-500 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                    title={preset.saved ? 'Overwrite existing preset' : 'Save to empty slot'}
                  >
                    <span className="text-lg">{index + 1}</span>
                    <span className="text-xs">{preset.saved ? 'Overwrite' : 'Empty'}</span>
                  </button>
                ))}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Control Panels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Synth Settings */}
          <div className="bg-gray-800 rounded-xl p-6">
            <SectionHeader title="Synthesizer" onReset={() => setSynthSettings(DEFAULT_SYNTH)} />

            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-2">Instrument</label>
              <select
                value={synthSettings.instrument}
                onChange={(e) => setSynthSettings(s => ({ ...s, instrument: e.target.value as InstrumentType }))}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="polysynth">PolySynth</option>
                <option value="fmsynth">FM Synth (Rhodes)</option>
                <option value="pluck">Pluck (Guitar/Harp)</option>
                <option value="piano" disabled={loadingPiano}>
                  {loadingPiano ? 'Piano (Loading...)' : pianoLoaded ? 'Grand Piano' : 'Grand Piano (Click to load)'}
                </option>
              </select>
              {synthSettings.instrument === 'piano' && !pianoLoaded && !loadingPiano && (
                <p className="text-xs text-yellow-500 mt-1">Piano samples will load when you press play</p>
              )}
              {loadingPiano && (
                <p className="text-xs text-blue-400 mt-1">Loading Salamander Grand Piano samples...</p>
              )}
            </div>

            {/* Only show oscillator type for polysynth */}
            {synthSettings.instrument === 'polysynth' && (
              <div className="mb-4">
                <label className="text-sm text-gray-400 block mb-2">Oscillator Type</label>
                <select
                  value={synthSettings.type}
                  onChange={(e) => setSynthSettings(s => ({ ...s, type: e.target.value as SynthSettings['type'] }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="sine">Sine</option>
                  <option value="square">Square</option>
                  <option value="sawtooth">Sawtooth</option>
                  <option value="triangle">Triangle</option>
                  <option value="fmsine">FM Sine</option>
                  <option value="amsine">AM Sine</option>
                </select>
              </div>
            )}

            <div className="space-y-4">
              <Slider label="Attack" value={synthSettings.attack} min={0.001} max={1} onChange={(v) => setSynthSettings(s => ({ ...s, attack: v }))} />
              <Slider label="Decay" value={synthSettings.decay} min={0.01} max={1} onChange={(v) => setSynthSettings(s => ({ ...s, decay: v }))} />
              <Slider label="Sustain" value={synthSettings.sustain} min={0} max={1} onChange={(v) => setSynthSettings(s => ({ ...s, sustain: v }))} />
              <Slider label="Release" value={synthSettings.release} min={0.01} max={3} onChange={(v) => setSynthSettings(s => ({ ...s, release: v }))} />
              <Slider label="Volume" value={synthSettings.volume} min={-40} max={0} step={1} unit=" dB" onChange={(v) => setSynthSettings(s => ({ ...s, volume: v }))} />
            </div>
          </div>

          {/* Filter Settings */}
          <div className="bg-gray-800 rounded-xl p-6">
            <SectionHeader title="Filter" onReset={() => setFilterSettings(DEFAULT_FILTER)} />

            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-2">Filter Type</label>
              <select
                value={filterSettings.type}
                onChange={(e) => setFilterSettings(s => ({ ...s, type: e.target.value as FilterSettings['type'] }))}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="lowpass">Low Pass</option>
                <option value="highpass">High Pass</option>
                <option value="bandpass">Band Pass</option>
              </select>
            </div>

            <div className="space-y-4">
              <Slider label="Cutoff Frequency" value={filterSettings.frequency} min={100} max={8000} step={10} unit=" Hz" onChange={(v) => setFilterSettings(s => ({ ...s, frequency: v }))} />
              <Slider label="Resonance (Q)" value={filterSettings.Q} min={0.1} max={10} onChange={(v) => setFilterSettings(s => ({ ...s, Q: v }))} />
            </div>
          </div>

          {/* Chord Progression */}
          <div className="bg-gray-800 rounded-xl p-6">
            <SectionHeader title="Chord Progression" onReset={() => setChordProgression('lofi-jazz')} />

            <div className="mb-4">
              <label className="text-sm text-gray-400 block mb-2">Progression Style</label>
              <select
                value={chordProgression}
                onChange={(e) => setChordProgression(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                {Object.keys(CHORD_PROGRESSIONS).map(key => (
                  <option key={key} value={key}>{key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">Chords in progression:</p>
              {CHORD_PROGRESSIONS[chordProgression]?.map((line, i) => (
                <p key={i} className="text-sm font-mono text-white">
                  {line.join(' → ')}
                </p>
              ))}
            </div>
          </div>

          {/* Drums */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-purple-400">Drums</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDrumSettings(DEFAULT_DRUMS)}
                  className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  title="Reset to defaults"
                >
                  Reset
                </button>
                <button
                  onClick={() => setDrumSettings(s => ({ ...s, enabled: !s.enabled }))}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    drumSettings.enabled ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {drumSettings.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <Slider label="Kick Volume" value={drumSettings.kickVolume} min={-40} max={0} step={1} unit=" dB" onChange={(v) => setDrumSettings(s => ({ ...s, kickVolume: v }))} disabled={!drumSettings.enabled} />
              <Slider label="Snare Volume" value={drumSettings.snareVolume} min={-40} max={0} step={1} unit=" dB" onChange={(v) => setDrumSettings(s => ({ ...s, snareVolume: v }))} disabled={!drumSettings.enabled} />
              <Slider label="Hi-Hat Volume" value={drumSettings.hihatVolume} min={-40} max={0} step={1} unit=" dB" onChange={(v) => setDrumSettings(s => ({ ...s, hihatVolume: v }))} disabled={!drumSettings.enabled} />
              <Slider label="Swing" value={drumSettings.swing} min={0} max={0.5} onChange={(v) => setDrumSettings(s => ({ ...s, swing: v }))} disabled={!drumSettings.enabled} />
            </div>
          </div>

          {/* Bass */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-purple-400">Bass</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBassSettings(DEFAULT_BASS)}
                  className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  title="Reset to defaults"
                >
                  Reset
                </button>
                <button
                  onClick={() => setBassSettings(s => ({ ...s, enabled: !s.enabled }))}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    bassSettings.enabled ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {bassSettings.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <Slider label="Volume" value={bassSettings.volume} min={-40} max={0} step={1} unit=" dB" onChange={(v) => setBassSettings(s => ({ ...s, volume: v }))} disabled={!bassSettings.enabled} />

              <div className="mb-4">
                <label className="text-sm text-gray-400 block mb-2">Octave Offset</label>
                <select
                  value={bassSettings.octave}
                  onChange={(e) => setBassSettings(s => ({ ...s, octave: parseInt(e.target.value) }))}
                  disabled={!bassSettings.enabled}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white disabled:opacity-50"
                >
                  <option value={-2}>-2 (Sub Bass)</option>
                  <option value={-1}>-1 (Standard)</option>
                  <option value={0}>0 (Higher)</option>
                </select>
              </div>

              <Slider label="Attack" value={bassSettings.attack} min={0.001} max={0.5} onChange={(v) => setBassSettings(s => ({ ...s, attack: v }))} disabled={!bassSettings.enabled} />
              <Slider label="Decay" value={bassSettings.decay} min={0.01} max={1} onChange={(v) => setBassSettings(s => ({ ...s, decay: v }))} disabled={!bassSettings.enabled} />
              <Slider label="Sustain" value={bassSettings.sustain} min={0} max={1} onChange={(v) => setBassSettings(s => ({ ...s, sustain: v }))} disabled={!bassSettings.enabled} />
              <Slider label="Release" value={bassSettings.release} min={0.01} max={2} onChange={(v) => setBassSettings(s => ({ ...s, release: v }))} disabled={!bassSettings.enabled} />
            </div>
          </div>

          {/* Arpeggiator */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-purple-400">Arpeggiator</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setArpSettings(DEFAULT_ARP)}
                  className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                  title="Reset to defaults"
                >
                  Reset
                </button>
                <button
                  onClick={() => setArpSettings(s => ({ ...s, enabled: !s.enabled }))}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                    arpSettings.enabled ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {arpSettings.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              {arpSettings.enabled ? 'Chords play as arpeggiated notes' : 'Chords play as block chords'}
            </p>

            <div className="space-y-4">
              <div className="mb-4">
                <label className="text-sm text-gray-400 block mb-2">Pattern</label>
                <select
                  value={arpSettings.pattern}
                  onChange={(e) => setArpSettings(s => ({ ...s, pattern: e.target.value as ArpSettings['pattern'] }))}
                  disabled={!arpSettings.enabled}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white disabled:opacity-50"
                >
                  <option value="up">Up ↑</option>
                  <option value="down">Down ↓</option>
                  <option value="upDown">Up/Down ↑↓</option>
                  <option value="random">Random</option>
                  <option value="randomOnce">Random (Fixed)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-400 block mb-2">Rate</label>
                <select
                  value={arpSettings.rate}
                  onChange={(e) => setArpSettings(s => ({ ...s, rate: e.target.value as ArpSettings['rate'] }))}
                  disabled={!arpSettings.enabled}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white disabled:opacity-50"
                >
                  <option value="4n">1/4 Note</option>
                  <option value="8n">1/8 Note</option>
                  <option value="8t">1/8 Triplet</option>
                  <option value="16n">1/16 Note</option>
                  <option value="16t">1/16 Triplet</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="text-sm text-gray-400 block mb-2">Octaves</label>
                <select
                  value={arpSettings.octaves}
                  onChange={(e) => setArpSettings(s => ({ ...s, octaves: parseInt(e.target.value) }))}
                  disabled={!arpSettings.enabled}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white disabled:opacity-50"
                >
                  <option value={1}>1 Octave</option>
                  <option value={2}>2 Octaves</option>
                  <option value={3}>3 Octaves</option>
                </select>
              </div>

              <Slider label="Velocity" value={arpSettings.velocity} min={0.1} max={1} onChange={(v) => setArpSettings(s => ({ ...s, velocity: v }))} disabled={!arpSettings.enabled} />
            </div>
          </div>

          {/* Effects */}
          <div className="bg-gray-800 rounded-xl p-6">
            <SectionHeader title="Effects" onReset={() => setEffectsSettings(DEFAULT_EFFECTS)} />

            <div className="space-y-4">
              <div className="border-b border-gray-700 pb-3 mb-3">
                <p className="text-xs text-gray-500 uppercase mb-2">Reverb</p>
                <Slider label="Wet" value={effectsSettings.reverbWet} min={0} max={1} onChange={(v) => setEffectsSettings(s => ({ ...s, reverbWet: v }))} />
                <Slider label="Decay" value={effectsSettings.reverbDecay} min={0.5} max={10} unit=" s" onChange={(v) => setEffectsSettings(s => ({ ...s, reverbDecay: v }))} />
              </div>

              <div className="border-b border-gray-700 pb-3 mb-3">
                <p className="text-xs text-gray-500 uppercase mb-2">Delay</p>
                <Slider label="Wet" value={effectsSettings.delayWet} min={0} max={1} onChange={(v) => setEffectsSettings(s => ({ ...s, delayWet: v }))} />
                <Slider label="Time" value={effectsSettings.delayTime} min={0.1} max={1} unit=" s" onChange={(v) => setEffectsSettings(s => ({ ...s, delayTime: v }))} />
                <Slider label="Feedback" value={effectsSettings.delayFeedback} min={0} max={0.9} onChange={(v) => setEffectsSettings(s => ({ ...s, delayFeedback: v }))} />
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase mb-2">Chorus</p>
                <Slider label="Wet" value={effectsSettings.chorusWet} min={0} max={1} onChange={(v) => setEffectsSettings(s => ({ ...s, chorusWet: v }))} />
                <Slider label="Frequency" value={effectsSettings.chorusFrequency} min={0.1} max={10} unit=" Hz" onChange={(v) => setEffectsSettings(s => ({ ...s, chorusFrequency: v }))} />
                <Slider label="Depth" value={effectsSettings.chorusDepth} min={0} max={1} onChange={(v) => setEffectsSettings(s => ({ ...s, chorusDepth: v }))} />
              </div>
            </div>
          </div>

          {/* Lofi Character */}
          <div className="bg-gray-800 rounded-xl p-6">
            <SectionHeader
              title="Lofi Character"
              onReset={() => {
                setEffectsSettings(s => ({ ...s, bitcrusherBits: DEFAULT_EFFECTS.bitcrusherBits, bitcrusherWet: DEFAULT_EFFECTS.bitcrusherWet }));
                setAmbientSettings(DEFAULT_AMBIENT);
              }}
            />

            <div className="space-y-4">
              <div className="border-b border-gray-700 pb-3 mb-3">
                <p className="text-xs text-gray-500 uppercase mb-2">Bitcrusher</p>
                <Slider label="Bits" value={effectsSettings.bitcrusherBits} min={4} max={16} step={1} onChange={(v) => setEffectsSettings(s => ({ ...s, bitcrusherBits: v }))} />
                <Slider label="Wet" value={effectsSettings.bitcrusherWet} min={0} max={1} onChange={(v) => setEffectsSettings(s => ({ ...s, bitcrusherWet: v }))} />
              </div>

              <div className="border-b border-gray-700 pb-3 mb-3">
                <p className="text-xs text-gray-500 uppercase mb-2">Vinyl Noise</p>
                <Slider label="Volume" value={ambientSettings.vinylNoiseVolume} min={-40} max={-6} step={1} unit=" dB" onChange={(v) => setAmbientSettings(s => ({ ...s, vinylNoiseVolume: v }))} />
              </div>

              <div>
                <p className="text-xs text-gray-500 uppercase mb-2">Tape Wobble</p>
                <Slider label="Amount" value={ambientSettings.tapeWobbleAmount} min={0} max={0.1} onChange={(v) => setAmbientSettings(s => ({ ...s, tapeWobbleAmount: v }))} />
              </div>
            </div>
          </div>
        </div>

        {/* Keyboard Visualizer */}
        <div className="bg-gray-800 rounded-xl p-6 mt-6">
          <h2 className="text-lg font-semibold text-purple-400 mb-4">Currently Playing Notes</h2>
          <div className="flex gap-1 overflow-x-auto pb-2">
            {currentChord && CHORD_NOTES[currentChord]?.map((note, i) => (
              <div
                key={i}
                className="px-4 py-3 bg-purple-500 rounded-lg text-white font-mono text-lg animate-pulse"
              >
                {note}
              </div>
            ))}
            {!currentChord && (
              <div className="text-gray-500 italic">Press play to start</div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="bg-gray-800/50 rounded-xl p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-400 mb-2">About</h2>
          <p className="text-gray-500 text-sm">
            This lofi music generator uses Tone.js for procedural audio synthesis.
            All music is generated in real-time using synthesizers, effects chains, and sequenced patterns.
            Adjust the controls to create your perfect lofi atmosphere.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-400">Tone.js</span>
            <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-400">Web Audio API</span>
            <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-400">Procedural Music</span>
            <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-400">React</span>
          </div>
        </div>
      </div>
    </div>
  );
}
